import {
  Component,
  inject,
  OnInit,
  signal,
  DestroyRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MaterialModule } from '../../../shared/material/material.module';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink, ActivatedRoute } from '@angular/router';
import { AvailabilityService } from '../../../shared/services/availability.service';
import { EnumDescriptionPipe } from '../../../shared/pipes/enum-description.pipe';
import { AvailabilityComponent } from '../../../shared/availability/availability.component';
import { ContainerComponent } from '../../../shared/container/container.component';
import { ResourceExamesEnum, ResourceTerapiaEnum } from '../../../shared/enums';
import { ProviderPJContextService } from '../../../shared/services/provider-pj-context.service';
import { ProfileProviderPJ, ResourceDemand, ResourceDemandControllerService } from '../../../core/generated';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, distinctUntilChanged } from 'rxjs/operators';

interface ResourceItem {
  key: string;
  description: string;
}

interface ResourceGroups {
  exames: ResourceItem[];
  terapias: ResourceItem[];
}

@Component({
    selector: 'app-resource-demand',
    standalone: true,
    imports: [
        CommonModule,
        MaterialModule,
        RouterLink,
        EnumDescriptionPipe,
        AvailabilityComponent,
        ContainerComponent,
    ],
    templateUrl: './resource-demand.component.html',
    styleUrls: ['./resource-demand.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourceDemandComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly resourceDemandService = inject(ResourceDemandControllerService);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly providerPJContextService = inject(ProviderPJContextService);
  private readonly availabilityService = inject(AvailabilityService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);

  resourceDemandList: ResourceDemand[] = [];
  providerPJ: ProfileProviderPJ | undefined;
  isLoading = false;

  readonly isMobile = this.deviceService.isMobile();
  readonly panelOpenState = signal(false);

  private currentCnpj: string | undefined;
  private readonly resourceGroupsCache = new Map<string, ResourceGroups>();

  ngOnInit(): void {
    this.setupContextSubscription();
    this.initialize();
  }

  private setupContextSubscription(): void {
    this.providerPJContextService.providerPJ$.pipe(
      filter((providerPJ): providerPJ is ProfileProviderPJ => providerPJ !== null),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((providerPJ) => {
      this.handleProviderPJChange(providerPJ);
    });
  }

  private handleProviderPJChange(providerPJ: ProfileProviderPJ): void {
    const hasChanged = !this.providerPJ || this.providerPJ.id !== providerPJ.id;

    if (hasChanged) {
      this.providerPJ = providerPJ;
      this.resourceDemandList = [];
      this.resourceGroupsCache.clear();

      if (providerPJ.cnpj) {
        this.loadResourceDemands();
      }

      this.cdr.markForCheck();
    }
  }

  async initialize(): Promise<void> {
    await this.providerPJContextService.ensureContextFromUrlIfMissing();

    const context = this.providerPJContextService.getProviderPJ();

    if (context) {
      this.providerPJ = context;

      if (context.cnpj && this.resourceDemandList.length === 0) {
        this.loadResourceDemands();
      }
    } else {
      this.redirectToProvider();
    }
  }

  private redirectToProvider(): void {
    const providerId = this.route.parent?.snapshot.paramMap.get('id');
    if (providerId) {
      this.router.navigate([`/providerPJ/${providerId}`]);
    }
  }

  loadResourceDemands(forceReload: boolean = false): void {
    if (!this.providerPJ?.cnpj) {
      return;
    }

    if (!forceReload && (this.isLoading || this.currentCnpj === this.providerPJ.cnpj)) {
      return;
    }

    this.isLoading = true;
    this.currentCnpj = this.providerPJ.cnpj;

    this.resourceDemandService
      .findBycnpj1(this.providerPJ.cnpj)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.resourceDemandList = response || [];

          if (this.resourceDemandList.length === 0) {
            this.navigateToResourceDemandForm();
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading resource demands:', error);
          this.resourceDemandList = [];
          this.cdr.markForCheck();
        },
        complete: () => {
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private navigateToResourceDemandForm(): void {
    const id = this.route.parent?.snapshot.paramMap.get('id');
    if (id) {
      this.router.navigate([`providerPJ/${id}/resourceDemand/resourceDemandForm`]);
    }
  }

  trackById(index: number, item: ResourceDemand): string | number {
    return item?.id ?? index;
  }

  publish(id: number): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.resourceDemandService
      .publish3(id, { publicationDate: '' })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading = false;
          // Recarregar a lista
          this.loadResourceDemands(true);
        },
        error: (error) => {
          console.error('Error publishing resource demand:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  deleteResourceDemand(id: number): void {
    if (this.isLoading) {
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();

    this.resourceDemandService
      .deleteById4(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading = false;
          // Recarregar a lista
          this.loadResourceDemands(true);
        },
        error: (error) => {
          console.error('Error deleting resource demand:', error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  getResourceGroups(resources: string[] | undefined): ResourceGroups {
    if (!resources || resources.length === 0) {
      return { exames: [], terapias: [] };
    }

    // Cache key based on resources array
    const cacheKey = resources.join(',');
    const cached = this.resourceGroupsCache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const exames: ResourceItem[] = [];
    const terapias: ResourceItem[] = [];

    resources.forEach(resource => {
      const exameDesc = this.getExameDescription(resource);
      if (exameDesc) {
        exames.push({ key: resource, description: exameDesc });
        return;
      }

      const terapiaDesc = this.getTerapiaDescription(resource);
      if (terapiaDesc) {
        terapias.push({ key: resource, description: terapiaDesc });
        return;
      }

      // Fallback: determine by business rules
      if (this.isTerapia(resource)) {
        terapias.push({ key: resource, description: resource });
      } else {
        exames.push({ key: resource, description: resource });
      }
    });

    const result: ResourceGroups = {
      exames: exames.sort((a, b) => a.description.localeCompare(b.description)),
      terapias: terapias.sort((a, b) => a.description.localeCompare(b.description))
    };

    // Store in cache
    this.resourceGroupsCache.set(cacheKey, result);
    return result;
  }

  getExameDescription(resourceKey: string): string | null {
    return (ResourceExamesEnum as any)[resourceKey] || null;
  }

  getTerapiaDescription(resourceKey: string): string | null {
    return (ResourceTerapiaEnum as any)[resourceKey] || null;
  }

  isTerapia(resourceKey: string): boolean {
    const terapiaKeys = ['HEMODIALISE', 'QUIMIOTERAPIA', 'RADIOTERAPIA'];
    return terapiaKeys.includes(resourceKey);
  }

  isLast(item: any, array: any[]): boolean {
    if (!array || array.length === 0) return true;
    return array.indexOf(item) === array.length - 1;
  }

  getResourcesDescription(resources: string[] | undefined): string {
    if (!resources || resources.length === 0) return '';

    return resources
      .map(res => this.description(res))
      .join(', ');
  }

  description(resourceKey: string): string {
    const exameDesc = this.getExameDescription(resourceKey);
    if (exameDesc) {
      return exameDesc;
    }

    const terapiaDesc = this.getTerapiaDescription(resourceKey);
    if (terapiaDesc) {
      return terapiaDesc;
    }

    return resourceKey;
  }
}
